/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Message } from '@squiz/dxp-ai-client';
import { formatDate } from '@squiz/dxp-console-support';
import {
  Card,
  EmptyState,
  ICON_DATA_OBJECT,
  ICON_SCHEDULE,
  ICON_TAG,
  ICON_THUMBS_UP_DOWN,
  Loader,
  SdsIcon,
} from '@squiz/sds';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { from, lastValueFrom } from 'rxjs';
import { AI_STRINGS } from '../../../Constants/Strings.constants';
import { getAiService } from '../../../Services/ai';
import { ThreadRatingWithIcon } from '../../AiThreadTable/utils/ThreadRatingWithIcon';
import { ThreadTagChips } from '../../AiThreadTable/utils/ThreadTagChips';
import { ViewThreadDialogContentProps } from './ViewThreadDialogContent.props';

/**
 * The thread details
 */
export const ViewThreadDialogContent = (props: ViewThreadDialogContentProps): JSX.Element => {
  const { thread } = props;

  const { t } = useTranslation();

  const labels = useMemo(() => {
    return {
      apiError: t(AI_STRINGS.apiError) as string,
      details: t(AI_STRINGS.details) as string,
      initiated: t(AI_STRINGS.initiated) as string,
      instructions: t(AI_STRINGS.instructions) as string,
      rating: t(AI_STRINGS.response) as string,
      response: t(AI_STRINGS.response) as string,
      tags: t(AI_STRINGS.tags) as string,
    };
  }, [t]);

  const aiService = getAiService();

  const [message, setMessage] = useState<Message | undefined>();
  const [messageError, setMessageError] = useState<string>();

  useEffect(() => {
    lastValueFrom(from(aiService.apiClient.generative.getGenerativeThreadQueryAndResponse(thread.aiId, thread.id)))
      .then((response) => {
        setMessage(response);
        return true;
      })
      .catch((e) => {
        setMessage(undefined);
        setMessageError(labels.apiError);
      });
  }, [aiService.apiClient.chatbotExecution, aiService.apiClient.generative, labels.apiError, thread.aiId, thread.id]);

  const messageBlock = useMemo(() => {
    if (messageError && message === undefined) {
      return <EmptyState caption={labels.apiError}></EmptyState>;
    }
    if (message) {
      return (
        <>
          <Card
            classes="view-thread-dialog-response-card"
            content={message.query || '-'}
            data-testid="query-card"
            format="standard"
            title="Input"
          />
          <Card
            classes="view-thread-dialog-response-card"
            content={message.response || '-'}
            data-testid="response-card"
            format="standard"
            title="Output"
          />
        </>
      );
    }
    return <Loader className="loader" type={'regular'}></Loader>;
  }, [labels.apiError, message, messageError]);

  return (
    <div className="view-thread-dialog-content">
      {/* Details */}
      <div className="view-thread-dialog-content-grey-box view-thread-dialog-content-details">
        <span className="view-thread-dialog-content-details-heading">{labels.details}</span>
        <div className="view-thread-dialog-content-white-box">
          {/* Initiated */}
          <div className="view-thread-dialog-content-heading-icon">
            <SdsIcon svg={ICON_SCHEDULE}></SdsIcon>
            <span>{labels.initiated}</span>
          </div>
          <div className="thread-details-block" data-testid="thread-date">
            {thread.timeLastInteracted !== null ? formatDate(thread.timeLastInteracted, true) : '-'}
          </div>
          {/* Rating */}
          <div className="view-thread-dialog-content-heading-icon">
            <SdsIcon svg={ICON_THUMBS_UP_DOWN}></SdsIcon>
            <span>{labels.rating}</span>
          </div>
          <div className="thread-details-block">
            <ThreadRatingWithIcon rating={thread.rating}></ThreadRatingWithIcon>
          </div>
          {/* Tags */}
          <div className="view-thread-dialog-content-heading-icon">
            <SdsIcon svg={ICON_TAG}></SdsIcon>
            <span>{labels.tags}</span>
          </div>
          <div className="thread-details-block">
            <ThreadTagChips tags={thread.tags ?? []}></ThreadTagChips>
          </div>
          {/* Instructions */}
          <div className="view-thread-dialog-content-heading-icon">
            <SdsIcon svg={ICON_DATA_OBJECT}></SdsIcon>
            <span>{labels.instructions}</span>
          </div>
          <div className="thread-details-block">{thread.instruction}</div>
        </div>
      </div>
      {/* Response / Conversation History */}
      <div className="view-thread-dialog-content-grey-box view-thread-dialog-content-response">
        <span className="view-thread-dialog-content-response-heading">{labels.response}</span>
        <div className="view-thread-dialog-response-white-box">{messageBlock}</div>
      </div>
    </div>
  );
};
